import React, { createContext, useContext, useEffect, useState } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_API_URL || '';

const getRedirectTarget = () => {
  const params = new URLSearchParams(window.location.search);
  const redirect = params.get('redirect');
  return redirect || '';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const fetchUser = async (authToken) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }
      const data = await response.json();
      setUser(data);
    } catch (error) {
      console.error('Error fetching user:', error);
      clearAuth();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('token=')) {
      const urlToken = hash.split('token=')[1].split('&')[0];
      if (urlToken) {
        localStorage.setItem('token', urlToken);
        setToken(urlToken);
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        const redirectTarget = getRedirectTarget();
        if (redirectTarget) {
          window.location.assign(redirectTarget);
          return;
        }
        fetchUser(urlToken);
        return;
      }
    }

    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      fetchUser(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const login = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/google`);
      if (!response.ok) {
        throw new Error('Failed to fetch auth URL');
      }
      const data = await response.json();
      window.location.href = data.auth_url;
    } catch (error) {
      console.error('Error initiating login:', error);
    }
  };

  const logout = () => {
    clearAuth();
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: Boolean(user),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
